import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

// Les mêmes bornes que le serveur, pour éviter un aller-retour dont la seule
// issue serait un 400. ⛔ Elles ne remplacent PAS la validation du serveur :
// c'est lui qui refuse, ceci ne fait qu'éviter de le déranger pour rien.
const MAX_PALIERS = 12;
const MAX_JOURS = 730;

/**
 * L'éditeur de paliers de relance.
 *
 * 🔑 La phrase en clair sous les jetons n'est pas décorative : c'est elle qui
 * fait comprendre le réglage. « 0, 7, 30 » ne dit rien à personne ; « 3
 * relances : le jour du retard, puis 7 jours après, puis 30 jours après.
 * Ensuite, plus rien » dit tout, y compris que ça s'arrête — c'était la
 * question de départ.
 */
export default function Paliers({ paliers, onChange, erreur }) {
  const { t } = useTranslation('myAccount');
  const [saisie, setSaisie] = useState('');

  const enClair = (jour) => (jour === 0
    ? t('notifications.day_zero')
    : t('notifications.days_after', { count: jour }));

  const ajouter = () => {
    const jour = parseInt(saisie, 10);
    if (Number.isNaN(jour) || jour < 0 || jour > MAX_JOURS) {
      return;
    }
    if (paliers.includes(jour) || paliers.length >= MAX_PALIERS) {
      setSaisie('');
      return;
    }
    // Trié ici aussi : le serveur le fait, mais voir « 30, 7 » une seconde
    // avant que la réponse arrive donne l'impression que l'ordre compte.
    onChange([...paliers, jour].sort((a, b) => a - b));
    setSaisie('');
  };

  const retirer = (jour) => {
    // ⛔ Jamais zéro palier : les rappels seraient allumés sans que rien ne
    // parte, et personne ne pourrait deviner pourquoi. Le serveur refuse aussi.
    if (paliers.length <= 1) {
      return;
    }
    onChange(paliers.filter((existant) => existant !== jour));
  };

  const resume = paliers.length === 1
    ? t('notifications.summary_one', { list: enClair(paliers[0]) })
    : t('notifications.summary_other', {
      count: paliers.length,
      list: paliers.map(enClair).join(', '),
    });

  return (
    <Box sx={{ ml: 6, mb: 3 }}>
      <Typography component="p" variant="body1" sx={{ fontWeight: 600 }}>
        {t('notifications.schedule')}
      </Typography>
      <Typography component="p" variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
        {t('notifications.schedule_help')}
      </Typography>

      <Box sx={{
        display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2,
      }}
      >
        {paliers.map((jour) => (
          <Chip
            key={jour}
            label={enClair(jour)}
            onDelete={paliers.length > 1 ? () => retirer(jour) : undefined}
            // Le nom accessible dit ce que la suppression enlève : « Retirer »
            // seul oblige à deviner lequel des jetons on va perdre.
            deleteIcon={undefined}
            aria-label={`${t('notifications.remove')} — ${enClair(jour)}`}
          />
        ))}
        <TextField
          variant="standard"
          type="number"
          value={saisie}
          placeholder={t('notifications.add_placeholder')}
          label={t('notifications.add')}
          inputProps={{ min: 0, max: MAX_JOURS, 'aria-label': t('notifications.add') }}
          sx={{ width: 160 }}
          onChange={(evenement) => setSaisie(evenement.target.value)}
          onBlur={ajouter}
          onKeyDown={(evenement) => {
            if (evenement.key === 'Enter') {
              evenement.preventDefault();
              ajouter();
            }
          }}
        />
      </Box>

      <Typography component="p" variant="body1">
        {resume}
      </Typography>
      {erreur && (
        <Typography component="p" variant="body1" color="error" sx={{ mt: 1 }}>
          {erreur}
        </Typography>
      )}
    </Box>
  );
}

Paliers.propTypes = {
  paliers: PropTypes.arrayOf(PropTypes.number).isRequired,
  onChange: PropTypes.func.isRequired,
  // eslint-disable-next-line react/require-default-props
  erreur: PropTypes.string,
};
